import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Alert from "@material-ui/lab/Alert";
import { useHistory } from "react-router-dom";
import moment from "moment-timezone";
import ListHeader from "./components/header";
import AddEventDialog from "./dialogs/add";
import { getAllFiltered } from "../api/programEvents";
import { getAll as getAllSeasons } from "../api/seasons";
import EventListCard from "./components/eventListCard";
import LoadingIndicator from "../../common/loadingIndicator";
import useDebounce from "../../common/useDebounce";
import { Badge } from "@material-ui/core";
import { useStyles } from "./styles";
import { EventStatus } from "../enums";
import { useDistrict } from "../hooks";

const DEFAULT_ELIGIBILITY_CRITERIA = {
    specialGroups: [],
    crusaderStudents: true,
    crusaderParents: false,
    guestStudents: false,
    guestParents: false,
    gold: false,
    silver: false,
    bronze: false,
    pewter: false,
    graduated: false,
    currentStudents: false,
    gradeNumbers: [],
    mathMin: 0,
    mathMax: 100,
    scienceMin: 0,
    scienceMax: 100,
    englishMin: 0,
    englishMax: 100,
    mathGradeType: 10,
    scienceGradeType: 10,
    englishGradeType: 10,
    institutions: [],
    schools: [],
    districts: [],
    cohortNumbers: [],
    zipCodes: [],
};

const toEventDateFilterIso = (date, boundary) => {
    const selectedDate = moment(date);
    if (!date || !selectedDate.isValid()) return null;

    return moment
        .tz(selectedDate.format("YYYY-MM-DD"), "America/New_York")
        [boundary]("day")
        .utc()
        .toISOString();
};

const getFilterStorageKey = (isActive, isComplete) => {
    if (isComplete) return "events-list-filters-complete";
    if (isActive) return "events-list-filters-active";
    return "events-list-filters-inactive";
};

const serializeFilters = (filters) =>
    JSON.stringify({
        ...filters,
        startDate: filters.startDate && moment(filters.startDate).isValid()
            ? moment(filters.startDate).toISOString()
            : null,
        endDate: filters.endDate && moment(filters.endDate).isValid()
            ? moment(filters.endDate).toISOString()
            : null,
    });

const deserializeFilters = (raw) => {
    const parsed = JSON.parse(raw);
    return {
        ...parsed,
        startDate: parsed.startDate ? moment(parsed.startDate) : null,
        endDate: parsed.endDate ? moment(parsed.endDate) : null,
    };
};

const loadPersistedFilters = (storageKey) => {
    try {
        const raw = sessionStorage.getItem(storageKey);
        if (!raw) return null;
        const filters = deserializeFilters(raw);
        return filters.filtersApplied ? filters : null;
    } catch {
        return null;
    }
};

const EventsList = ({ isActive, isComplete }) => {
    const classes = useStyles();
    const history = useHistory();
    const filterStorageKey = getFilterStorageKey(isActive, isComplete);
    const persistedFilters = useMemo(() => loadPersistedFilters(filterStorageKey), [filterStorageKey]);

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [seasons, setSeasons] = useState([]);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [reloadData, reload] = useState(true);
    const [department, setDepartment] = useState(persistedFilters?.department ?? "");
    const [status, setStatus] = useState(persistedFilters?.status ?? "");
    const [seasonId, setSeasonId] = useState(persistedFilters?.seasonId ?? "");
    const [searchText, setSearchText] = useState(persistedFilters?.searchText ?? "");
    const [grades, setGrades] = useState(persistedFilters?.grades ?? []);
    const [districts, setDistricts] = useState(persistedFilters?.districts ?? []);
    const [zipCodes, setZipCodes] = useState(persistedFilters?.zipCodes ?? []);
    const [eligibilityCriteria, setEligibilityCriteria] = useState(persistedFilters?.eligibilityCriteria ?? null);
    const [datePreset, setDatePreset] = useState(persistedFilters?.datePreset ?? "");
    const [startDate, setStartDate] = useState(persistedFilters?.startDate ?? null);
    const [endDate, setEndDate] = useState(persistedFilters?.endDate ?? null);
    const [filtersApplied, setFiltersApplied] = useState(persistedFilters?.filtersApplied ?? false);

    const { districtList } = useDistrict();

    const debouncedSearchValue = useDebounce(searchText, 350);
    const maxItems = 2;

    const handleSearchTextChange = (value) => {
        setSearchText(value);
        setFiltersApplied(true);
    };
    const handleSeasonIdChange = (value) => {
        setSeasonId(value);
        setFiltersApplied(true);
    };
    const handleDepartmentChange = (value) => {
        setDepartment(value);
        setFiltersApplied(true);
    };
    const handleStatusChange = (value) => {
        setStatus(value);
        setFiltersApplied(true);
    };
    const handleGradesChange = (value) => {
        setGrades(value);
        setFiltersApplied(true);
    };
    const handleDistrictsChange = (value) => {
        setDistricts(value);
        setFiltersApplied(true);
    };
    const handleZipCodesChange = (value) => {
        // Sanitize and dedupe entries before storing.
        const sanitized = (value || [])
            .map((z) => (z ?? "").toString().trim())
            .filter((z) => z.length > 0);
        const deduped = Array.from(new Set(sanitized));
        setZipCodes(deduped);
        setFiltersApplied(true);
    };
    const handleEligibilityCriteriaChange = (value) => {
        setEligibilityCriteria(value);
        setFiltersApplied(true);
    };
    const handleStartDateChange = (value) => {
        setStartDate(value);
        setDatePreset(value || endDate ? "custom" : "");
        setFiltersApplied(true);

        if (value && endDate && moment(value).isValid() && moment(endDate).isValid() && moment(value).isAfter(endDate, "day")) {
            setEndDate(value);
        }
    };
    const handleEndDateChange = (value) => {
        setEndDate(value);
        setDatePreset(startDate || value ? "custom" : "");
        setFiltersApplied(true);
    };
    const handleDatePresetChange = (value) => {
        setDatePreset(value);
        setFiltersApplied(true);

        if (!value) {
            setStartDate(null);
            setEndDate(null);
            return;
        }

        const now = moment.tz("America/New_York");
        let quarterStart;

        if (value === "previousQuarter") {
            quarterStart = now.clone().subtract(1, "quarter").startOf("quarter");
        } else if (value === "currentQuarter") {
            quarterStart = now.clone().startOf("quarter");
        } else if (value === "nextQuarter") {
            quarterStart = now.clone().add(1, "quarter").startOf("quarter");
        } else {
            const [, quarter, year] = value.match(/^q([1-4])-(\d{4})$/) || [];
            if (!quarter || !year) return;
            quarterStart = moment.tz({ year: Number(year), month: (Number(quarter) - 1) * 3, date: 1 }, "America/New_York");
        }

        setStartDate(quarterStart);
        setEndDate(quarterStart.clone().endOf("quarter"));
    };
    const handleClearFilters = () => {
        setSearchText("");
        setSeasonId("");
        setDepartment("");
        setStatus("");
        setGrades([]);
        setDistricts([]);
        setZipCodes([]);
        setEligibilityCriteria(null);
        setDatePreset("");
        setStartDate(null);
        setEndDate(null);
        setData([]);
        setFiltersApplied(false);
        sessionStorage.removeItem(filterStorageKey);
    };

    useEffect(() => {
        if (!filtersApplied) {
            sessionStorage.removeItem(filterStorageKey);
            return;
        }

        sessionStorage.setItem(
            filterStorageKey,
            serializeFilters({
                department,
                status,
                seasonId,
                searchText,
                grades,
                districts,
                zipCodes,
                eligibilityCriteria,
                datePreset,
                startDate,
                endDate,
                filtersApplied,
            })
        );
    }, [
        filterStorageKey,
        department,
        status,
        seasonId,
        searchText,
        grades,
        districts,
        zipCodes,
        eligibilityCriteria,
        datePreset,
        startDate,
        endDate,
        filtersApplied,
    ]);

    useEffect(() => {
        const seasonsTokenSource = axios.CancelToken.source();
        (async () => {
            try {
                const seasonsResponse = await getAllSeasons(null, seasonsTokenSource.token);
                setSeasons(seasonsResponse.data.result);
            } catch (ex) {}
        })();
        return () => seasonsTokenSource.cancel();
    }, []);

    useEffect(() => {
        if (!filtersApplied) return;

        const tokenSource = axios.CancelToken.source();
        let stat = status;
        if (isComplete) {
            setStatus(EventStatus.Complete);
            stat = EventStatus.Complete;
        }

        // Merge top-level district, grade, and zip code filters into eligibilityCriteria
        let mergedCriteria = eligibilityCriteria;
        if (districts.length > 0 || grades.length > 0 || zipCodes.length > 0) {
            const districtPayload = districts.map((id) => ({ districtId: id }));
            const gradePayload = grades.map((g) => ({ gradeNumber: g }));
            const zipCodePayload = zipCodes.map((z) => ({ zipCode: z }));
            mergedCriteria = {
                ...DEFAULT_ELIGIBILITY_CRITERIA,
                ...(eligibilityCriteria || {}),
                ...(districts.length > 0 && { districts: districtPayload }),
                ...(grades.length > 0 && { gradeNumbers: gradePayload }),
                ...(zipCodes.length > 0 && { zipCodes: zipCodePayload }),
            };
        }

        (async () => {
            try {
                setLoading(true);
                const listResponse = await getAllFiltered(
                    isActive,
                    debouncedSearchValue,
                    seasonId,
                    department,
                    stat,
                    mergedCriteria,
                    toEventDateFilterIso(startDate, "startOf"),
                    toEventDateFilterIso(endDate, "endOf"),
                    tokenSource.token
                );
                let res = [];
                if (isActive && !isComplete) {
                    res = listResponse.data.result.filter(flt => flt.status != EventStatus.Complete);
                } else {
                    res = listResponse.data.result;
                }
                setData(res);
            } catch (ex) {
            } finally {
                setLoading(false);
            }
        })();

        return () => {
            tokenSource.cancel();
        };
    }, [debouncedSearchValue, department, isActive, isComplete, reloadData, seasonId, status, eligibilityCriteria, districts, grades, zipCodes, startDate, endDate, filtersApplied]);

    const handleCloseAddDialog = (shouldReload, id) => {
        setShowAddDialog(false);
        if (id) {

            const getNavLink = () => {

                if (isActive) {
                    if (isComplete) {
                        return `/events/complete/event/${id}`
                    } else {
                        return `/events/active/event/${id}`
                    }

                } else {
                    return `/events/inactive/event/${id}`
                }

            }

            history.push(getNavLink());
            return;
        }

        if (shouldReload) {
            reload((prev) => !prev);
        }
    };

    return (
        <div className={classes.eventsList}>
            <LoadingIndicator loading={loading} />
            <ListHeader
                onAdd={() => setShowAddDialog(true)}
                seasons={seasons}
                isActive={isActive}
                seasonId={seasonId}
                onSeasonIdChange={handleSeasonIdChange}
                department={department}
                onDepartmentChange={handleDepartmentChange}
                status={status}
                onStatusChange={handleStatusChange}
                grades={grades}
                onGradesChange={handleGradesChange}
                districts={districts}
                districtList={districtList}
                onDistrictsChange={handleDistrictsChange}
                zipCodes={zipCodes}
                onZipCodesChange={handleZipCodesChange}
                searchText={searchText}
                onSearchTextChange={handleSearchTextChange}
                eligibilityCriteria={eligibilityCriteria}
                setEligibilityCriteria={handleEligibilityCriteriaChange}
                datePreset={datePreset}
                onDatePresetChange={handleDatePresetChange}
                startDate={startDate}
                onStartDateChange={handleStartDateChange}
                endDate={endDate}
                onEndDateChange={handleEndDateChange}
                filtersApplied={filtersApplied}
                onClearFilters={handleClearFilters}
            />
            {!filtersApplied && !loading && (
                <Alert severity="info">Select at least one filter to view events.</Alert>
            )}
            {filtersApplied && data.length !== 0 && (
                <div className={classes.list}>
                    {data.map((item, idx) => (
                        <Badge
                            badgeContent={`+${item.eventDates.length - maxItems}`}
                            invisible={maxItems >= item.eventDates.length}
                            color="primary"
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            key={idx}
                        >
                            <EventListCard event={item} isActive={isActive} isComplete={isComplete} />
                        </Badge>
                    ))}
                </div>
            )}
            {filtersApplied && data.length === 0 && !loading && <Alert severity="warning">No Events Found!</Alert>}

            {showAddDialog && <AddEventDialog open={showAddDialog} onClose={handleCloseAddDialog} />}
        </div>
    );
};

export default EventsList;
